import SectionVisible from 'appContainer/SectionVisible';
import { useClaimHook } from 'hooks/claim.hook';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { MarkdownRenderer, Typography } from 'styles';
import Paper from 'styles/paper/paper';
import Stack from 'styles/stack/Stack';
import CompositeTypography from 'styles/typography/CompositeTypography.styles';
import { SECTIONS } from 'types/SectionsVisibility';
import { useTranslation } from 'utils/translations';
import { formatMarkdown } from 'utils/utils';
import ClaimReturnDetails from './ClaimReturn/ClaimReturnDetails.component';
import ClaimStatusBreadcrumbs from './ClaimStatusBreadcrumbs.component';

const ClaimMemberDetails = () => {
	const { claimData: claim } = useSelector((store: RootState) => store.claim);
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: 'dbLabels',
	});

	const {
		showInternalNote,
		showPendingNote,
		showRejectedNote,
		showReturnStatus,
	} = useClaimHook();

	return (
		<Paper
			elevation={0}
			sx={{
				p: 2.5,
				justifyContent: 'space-between',
				display: 'flex',
				borderRadius: 0.5,
				width: '100%',
			}}
		>
			<Stack direction={'row'} width={'100%'} justifyContent={'space-between'}>
				<Stack rowGap={1.875} minWidth={'fit-content'}>
					<CompositeTypography
						label={tClientLabels('claim.labels.memberFullName')}
						value={claim?.beneficiaryFullName as string}
					/>
					<CompositeTypography
						label={tClientLabels('claim.labels.memberId')}
						value={claim?.beneficiaryMemberId as string}
					/>
					<CompositeTypography
						label={tClientLabels('claim.labels.memberDesignation')}
						value={tDbLabels(claim?.memberDesignationLabel as string)}
					/>
					{claim?.memberDesignation === 'Dependant' && (
						<CompositeTypography
							label={tClientLabels('claim.labels.subscriberName')}
							value={tDbLabels(claim?.memberFullName as string)}
						/>
					)}
					{claim.claimCarrierIntegrationInfo.carrierClaimId && (
						<CompositeTypography
							label={tClientLabels('claim.labels.carrierClaimId')}
							value={tDbLabels(
								claim?.claimCarrierIntegrationInfo.carrierClaimId as string,
							)}
						/>
					)}
				</Stack>
				<Stack flexGrow={0} width={'50%'} rowGap={1.875} pl={'20px'}>
					<CompositeTypography
						label={tClientLabels('claim.labels.planName')}
						value={claim?.planName as string}
					/>
					<CompositeTypography
						label={tClientLabels('claim.labels.memberProviderLocation')}
						value={claim?.memberProviderLocation as string}
					/>
					<CompositeTypography
						label={tClientLabels('claim.labels.status')}
						value={claim?.status.label as string}
						children={<ClaimStatusBreadcrumbs claim={claim} />}
					/>

					{showInternalNote && (
						<CompositeTypography
							label={tClientLabels('claim.labels.note')}
							value={claim?.status.internalNote as string}
							children={
								<Typography
									variant="body"
									sx={{
										'& > p': { padding: 0, margin: 0 },
										'& > ul': { margin: 0, paddingLeft: '20px' },
										'& > ul > li > p': { padding: 0, margin: 0 },
									}}
								>
									<MarkdownRenderer>
										{formatMarkdown(claim?.status.internalNote as string)}
									</MarkdownRenderer>
								</Typography>
							}
						/>
					)}
					{(showPendingNote || showRejectedNote) && (
						<>
							<SectionVisible
								sectionName={SECTIONS.ENABLE_CLAIM_REJECTION_DEFAULT_FIELDS}
								carrier={{ customLayout: claim.customLayout }}
							>
								<CompositeTypography
									label={tClientLabels('claim.labels.argument')}
									value={claim?.status.internalNote as string}
									children={
										<Typography
											variant="body"
											sx={{
												'& > p': { padding: 0, margin: 0 },
												'& > ul': { margin: 0, paddingLeft: '20px' },
												'& > ul > li > p': { padding: 0, margin: 0 },
											}}
										>
											<MarkdownRenderer>
												{formatMarkdown(claim?.status.internalNote as string)}
											</MarkdownRenderer>
										</Typography>
									}
								/>
							</SectionVisible>
							<SectionVisible
								sectionName={SECTIONS.ENABLE_CLAIM_REJECTION_ADDITIONAL_FIELDS}
								carrier={{ customLayout: claim.customLayout }}
							>
								<CompositeTypography
									label={
										showRejectedNote
											? tClientLabels('claim.labels.denialReason')
											: tClientLabels('claim.labels.argument')
									}
									value={claim?.status.internalNote as string}
									children={
										<Typography
											variant="body"
											sx={{
												'& > p': { padding: 0, margin: 0 },
												'& > ul': { margin: 0, paddingLeft: '20px' },
												'& > ul > li > p': { padding: 0, margin: 0 },
											}}
										>
											<MarkdownRenderer>
												{formatMarkdown(claim?.status.internalNote as string)}
											</MarkdownRenderer>
										</Typography>
									}
								/>
							</SectionVisible>
						</>
					)}
					{showReturnStatus && <ClaimReturnDetails />}
				</Stack>
			</Stack>
		</Paper>
	);
};

export default ClaimMemberDetails;
